import { createQueryKeyStore } from "@lukemorales/query-key-factory";
import { skipToken } from "@tanstack/react-query";

import type { Category } from "@/shared/domains";
import type { MakeOptional } from "@/shared/lib/types";
import type { UserAttributes } from "@/shared/models";
import { formatDOB } from "@/shared/utils";
import type { Product } from "../models";
import type { GetProductsByCategoryParams } from "./category";
import {
  getPatientCategories,
  getPatientProductsByCategory,
  getProductCategories,
  getProductsByCategory,
} from "./category";
import type { GetSummaryParams } from "./shop";
import {
  getCategoriesWithProducts,
  getProductsDiscount,
  getSummary,
} from "./shop";
import { getAvailableProductsToSubscribe } from "./subscription";

export const productsQueriesStore = createQueryKeyStore({
  products: {
    category: {
      queryKey: null,
      contextQueries: {
        list: (params?: UserAttributes) => ({
          queryKey: [{ params }],
          queryFn: params
            ? () =>
                getProductCategories({
                  ...params,
                  dateOfBirth: formatDOB(params.dateOfBirth),
                })
            : (skipToken as never),
        }),

        products: (params: GetProductsByCategoryParams) => ({
          queryKey: [params],
          queryFn: () => getProductsByCategory(params),
        }),
      },
    },

    shop: {
      queryKey: null,
      queryFn: getCategoriesWithProducts,
      contextQueries: {
        discounts: (productId?: Product["id"]) => ({
          queryKey: [productId],
          queryFn: productId
            ? () => getProductsDiscount(productId)
            : (skipToken as never),
        }),
        summary: ({
          priceId,
          ...params
        }: MakeOptional<GetSummaryParams, "priceId">) => ({
          queryKey: [{ priceId, ...params }],
          queryFn:
            priceId !== undefined
              ? () => getSummary({ priceId, ...params })
              : (skipToken as never),
        }),
      },
    },

    patient: {
      queryKey: null,
      contextQueries: {
        categories: {
          queryKey: null,
          queryFn: getPatientCategories,
        },

        products: (categoryId?: Category["id"]) => ({
          queryKey: [categoryId],
          queryFn: categoryId
            ? () => getPatientProductsByCategory(categoryId)
            : (skipToken as never),
        }),
      },
    },

    available: {
      queryKey: null,
      queryFn: getAvailableProductsToSubscribe,
    },
  },
});
